import {
  ADD_DEVICE_FLOW_TITLE,
  DEVICE_SETUP_TIMING,
  PROGRESS_RAIL_STEPS,
} from '@/config/addDeviceFlow';
import { staticDefaults } from '@/config/static/catalog';
import { STORAGE_KEYS, WORKFLOW_QUERY } from '@/utils/constants';
import type { DeviceDraft, DeviceSetupSuccess, FormStepId } from '@/types/deviceSetup';
import {
  buildDeviceSetupNavigateUrl,
  createLocalDeviceSetupConversationId,
  isDeviceSetupConversation,
  registerDeviceSetupConversation,
} from '@/utils/conversationWorkflow';
import { hasParsedInputPrefill, mergePartialDraft } from '@/utils/draftPrefill';
import { FLOW_CONFIRM_APPROVE_MESSAGE } from '@/utils/flowConfirmCta';
import type { GuidedFlowRuntime } from './types';

const OPERATOR_PASSWORD_PREFILL_HINT = staticDefaults.shared.operatorPasswordPrefillHint;

const DEVICE_AI_FIELD_KEYS = [
  'name',
  'manufacturer',
  'model',
  'deviceServer',
  'connectionType',
  'ipAddress',
  'port',
  'username',
] as const;

function deviceHasParsedDetails(draft: DeviceDraft): boolean {
  return Boolean(draft.manufacturer && draft.model && draft.name && draft.ipAddress);
}

function deviceFormStepForDraft(draft: DeviceDraft, hasParsedPrefill: boolean): FormStepId {
  if (hasParsedPrefill && deviceHasParsedDetails(draft)) return 'details';
  if (hasParsedPrefill && draft.manufacturer && draft.model) return 'manufacturer';
  if (hasParsedPrefill && draft.category) return 'manufacturer';
  return 'device-type';
}

const FORM_STEP_ORDER: FormStepId[] = ['device-type', 'manufacturer', 'details', 'confirm'];
const LOADING_PHASES = ['thinking', 'working', 'almost-ready', 'preview'] as const;

function emptyDraft(): DeviceDraft {
  return {
    category: null,
    manufacturer: '',
    model: '',
    name: '',
    description: '',
    ipAddress: '',
    port: '3001',
    macAddress: '',
    deviceServer: 'localhost',
    username: '',
    password: '',
    connectionType: 'HTTP',
  };
}

export const deviceSetupRuntime: GuidedFlowRuntime<DeviceDraft, FormStepId, DeviceSetupSuccess> = {
  id: 'device-setup',
  storageKeyPrefix: STORAGE_KEYS.DEVICE_SETUP_STATE,
  workflowQuery: WORKFLOW_QUERY.DEVICE_SETUP,
  flowTitle: ADD_DEVICE_FLOW_TITLE,
  progressRailSteps: PROGRESS_RAIL_STEPS,
  timing: DEVICE_SETUP_TIMING,
  loadingPhases: LOADING_PHASES,
  formStepOrder: FORM_STEP_ORDER,
  initialFormStep: 'device-type',
  explainabilityText: staticDefaults.device.explainability,
  formMessageAssistantHint: staticDefaults.device.assistantHint,
  idleHint: 'Describe the controller in the box below, then click the arrow to send.',
  savingTitle: 'Saving',
  loadingMessage: 'Preparing guided device setup…',
  start: {
    conversationTitle: 'New controller onboarding',
    defaultStarterMessage: 'Add a Mercury LP4502 controller for this site',
    clearStoredState: (conversationId) => {
      localStorage.removeItem(`${STORAGE_KEYS.DEVICE_SETUP_STATE}:${conversationId}`);
    },
    registerConversation: registerDeviceSetupConversation,
    createLocalConversationId: createLocalDeviceSetupConversationId,
    buildNavigateUrl: buildDeviceSetupNavigateUrl,
    isConversationRegistered: isDeviceSetupConversation,
  },
  emptyDraft,
  createIdleState: (conversationId, userRole) => ({
    conversationId,
    flowId: 'device-setup',
    workflowStatus: 'draft',
    uiPhase: 'idle',
    statusLabel: '',
    progressRail: PROGRESS_RAIL_STEPS.map((s) => ({ id: s.id, label: s.label, status: 'pending' as const })),
    formStep: 'device-type',
    draft: emptyDraft(),
    userMessages: [],
    assistantHint: '',
    showExplainability: false,
    explainabilityText: staticDefaults.device.explainability,
    showFormWizard: false,
    showAiBanner: false,
    aiFilledFields: [],
    userRole,
    result: null,
  }),
  hydrateStored: (saved) => {
    const withBanner = { ...saved, showAiBanner: saved.showAiBanner ?? false };
    if (!LOADING_PHASES.includes(withBanner.uiPhase as (typeof LOADING_PHASES)[number])) {
      return withBanner;
    }
    if (withBanner.userMessages.length === 0) {
      return deviceSetupRuntime.createIdleState(withBanner.conversationId, withBanner.userRole);
    }
    const hasParsedPrefill = hasParsedInputPrefill(saved.aiFilledFields);
    const draft =
      saved.draft.category || hasParsedPrefill
        ? saved.draft
        : { ...emptyDraft(), category: 'door-controller' as const };
    return {
      ...withBanner,
      uiPhase: 'form',
      formStep: deviceFormStepForDraft(draft, hasParsedPrefill),
      statusLabel: ADD_DEVICE_FLOW_TITLE,
      progressRail: saved.progressRail.map((s) => ({ ...s, status: 'completed' as const })),
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      assistantHint: hasParsedPrefill
        ? OPERATOR_PASSWORD_PREFILL_HINT
        : staticDefaults.device.assistantHint,
      draft,
      aiFilledFields: saved.aiFilledFields ?? [],
      showAiBanner: saved.showAiBanner ?? hasParsedPrefill,
    };
  },
  buildFormReveal: ({ base, siteLabel, userRole }) => {
    const isOperator = userRole === 'operator';
    const hasParsedPrefill = hasParsedInputPrefill(base.aiFilledFields);
    const defaults: DeviceDraft =
      hasParsedPrefill || !isOperator
        ? { ...emptyDraft(), category: 'door-controller' }
        : { ...staticDefaults.device.defaultDraft };
    const formDraft = mergePartialDraft<DeviceDraft>(defaults, base.draft);
    const aiFields = hasParsedPrefill
      ? base.aiFilledFields
      : isOperator
        ? [...DEVICE_AI_FIELD_KEYS]
        : [];
    const hint =
      hasParsedPrefill || isOperator
        ? OPERATOR_PASSWORD_PREFILL_HINT
        : staticDefaults.device.assistantHint.replace('First floor', siteLabel);
    return {
      uiPhase: 'form',
      formStep: deviceFormStepForDraft(formDraft, hasParsedPrefill),
      statusLabel: ADD_DEVICE_FLOW_TITLE,
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      showExplainability: false,
      showAiBanner: aiFields.length > 0,
      assistantHint: hint,
      introAssistantHint: hint,
      draft: formDraft,
      aiFilledFields: aiFields,
    };
  },
  patchDraftOnFormMessage: (draft, userRole) => {
    if (userRole !== 'operator') return draft;
    return {
      ...draft,
      name: draft.name || staticDefaults.device.defaultDraft.name,
      manufacturer: draft.manufacturer || staticDefaults.device.defaultDraft.manufacturer,
      model: draft.model || staticDefaults.device.defaultDraft.model,
      ipAddress: draft.ipAddress || staticDefaults.device.defaultDraft.ipAddress,
    };
  },
  isConfirmReady: (draft) =>
    Boolean(draft.category) &&
    Boolean(draft.manufacturer) &&
    Boolean(draft.model) &&
    Boolean(draft.name) &&
    Boolean(draft.ipAddress),
  isStepNextDisabled: (stepId, draft) => {
    if (stepId === 'device-type') return !draft.category;
    if (stepId === 'manufacturer') return !draft.manufacturer || !draft.model;
    if (stepId === 'details') return !draft.name || !draft.ipAddress;
    return false;
  },
  getAiFilledFields: (draft, userRole) => {
    if (!draft.manufacturer || !draft.model || !draft.name) return [];
    if (userRole === 'operator') return [...DEVICE_AI_FIELD_KEYS];
    return [];
  },
  shouldShowExplainability: () => true,
  buildApproveUserMessage: () => FLOW_CONFIRM_APPROVE_MESSAGE,
  buildSuccess: () => ({
    result: {
      controllerName: staticDefaults.device.success.controllerName,
      summaryLine: staticDefaults.device.success.summaryLine,
      nextSteps: [...staticDefaults.device.success.nextSteps],
    },
    assistantHint:
      'Controller 1 is now in this system. Here’s a quick summary and what you can do next.',
  }),
};
